// client/src/components/map/assetMarkerImages.ts
//
// The asset marker (map/assetMarker, ST-00244 option C) on a maplibre map, as symbol layers.
// One marker is three images on three layers, so the pictogram never turns:
//
//   shadow — a soft silhouette, turned to the heading, offset downward with `icon-translate`
//            (viewport-anchored, so the shadow falls down whatever the heading);
//   nose   — the white-keylined disc with its pointed nose, turned with `icon-rotate`
//            (moving assets only);
//   face   — the shaded disc and the upright pictogram (`icon-rotation-alignment: viewport`).
//
// Each feature carries the image names it needs (assetMarkerFeatureProps); a name says
// everything needed to draw it (`asset-marker/face/truck/1e40af/solid/nose/right/40`), so the
// images are drawn on demand from `styleimagemissing` — after load and after every basemap
// swap (setStyle wipes images). SVG decodes asynchronously: a transparent placeholder of the
// right size goes in at once, and the real image replaces it (updateImage) once decoded.
// Cluster bubbles (`asset-marker/cluster/<n>`) are drawn the same way, so the counts need no
// glyphs endpoint (the satellite raster style has none).

import type {
  CircleLayerSpecification,
  ExpressionSpecification,
  FilterSpecification,
  Map as MaplibreMap,
  SymbolLayerSpecification,
} from 'maplibre-gl';
import {
  ASSET_MARKER_DEFAULT_COLOR,
  ASSET_MARKER_HALO,
  ASSET_MARKER_OUTLINE,
  assetMarkerFacing,
  assetMarkerHasNose,
  assetMarkerSvg,
  normaliseHeading,
  normaliseHex,
  type AssetMarkerFacing,
  type AssetMarkerKind,
} from './assetMarker';
import { ASSET_STATUS, type AssetStatusKey } from './assetStatus';

export const ASSET_MARKER_IMAGE_PREFIX = 'asset-marker/';
export const ASSET_MARKER_PIXEL_RATIO = 2;
/** The selection ring colour (the app's teal-500, as on the old live map). */
export const ASSET_MARKER_SELECTED = '#14b8a6';

export type AssetMarkerImageSpec =
  | {
      part: 'face';
      kind: AssetMarkerKind;
      /** rrggbb, no '#'. */
      color: string;
      hollow: boolean;
      nose: boolean;
      facing: AssetMarkerFacing;
      size: number;
    }
  | { part: 'nose'; color: string; size: number }
  | { part: 'shadow'; shape: 'puck' | 'disc'; size: number }
  | { part: 'cluster'; count: number };

/** The image name for a spec. */
export function assetMarkerImageId(spec: AssetMarkerImageSpec): string {
  const p = ASSET_MARKER_IMAGE_PREFIX;
  switch (spec.part) {
    case 'face':
      return `${p}face/${spec.kind}/${spec.color}/${spec.hollow ? 'hollow' : 'solid'}/${spec.nose ? 'nose' : 'still'}/${spec.facing}/${spec.size}`;
    case 'nose':
      return `${p}nose/${spec.color}/${spec.size}`;
    case 'shadow':
      return `${p}shadow/${spec.shape}/${spec.size}`;
    default:
      return `${p}cluster/${spec.count}`;
  }
}

const SIZE_OK = (n: number) => Number.isInteger(n) && n >= 8 && n <= 128;
const HEX6 = /^[0-9a-f]{6}$/;

/** The spec behind an image name, or null when it is not one of ours (or malformed). */
export function parseAssetMarkerImageId(id: string): AssetMarkerImageSpec | null {
  if (!id.startsWith(ASSET_MARKER_IMAGE_PREFIX)) return null;
  const [part, ...rest] = id.slice(ASSET_MARKER_IMAGE_PREFIX.length).split('/');
  if (part === 'face' && rest.length === 6) {
    const [kind, color, fill, nose, facing, sizeText] = rest as [
      string,
      string,
      string,
      string,
      string,
      string,
    ];
    const size = Number(sizeText);
    if (
      (kind === 'truck' || kind === 'loader') &&
      HEX6.test(color) &&
      (fill === 'solid' || fill === 'hollow') &&
      (nose === 'nose' || nose === 'still') &&
      (facing === 'left' || facing === 'right') &&
      SIZE_OK(size)
    ) {
      return { part, kind, color, hollow: fill === 'hollow', nose: nose === 'nose', facing, size };
    }
    return null;
  }
  if (part === 'nose' && rest.length === 2) {
    const [color, sizeText] = rest as [string, string];
    const size = Number(sizeText);
    return HEX6.test(color) && SIZE_OK(size) ? { part, color, size } : null;
  }
  if (part === 'shadow' && rest.length === 2) {
    const [shape, sizeText] = rest as [string, string];
    const size = Number(sizeText);
    return (shape === 'puck' || shape === 'disc') && SIZE_OK(size) ? { part, shape, size } : null;
  }
  if (part === 'cluster' && rest.length === 1) {
    const count = Number(rest[0]);
    return Number.isInteger(count) && count > 0 && count < 100000 ? { part, count } : null;
  }
  return null;
}

// ── Cluster bubble ───────────────────────────────────────────────────────────────────────────
/** Bubble diameter (CSS px) for a count. */
export const clusterDiameter = (count: number): number => (count < 10 ? 32 : count < 100 ? 36 : 42);
/** The bubble image's box (CSS px): the diameter plus room for the halo and shadow. */
const clusterBox = (count: number) => clusterDiameter(count) + 10;

/** A dark disc with a white keyline, a soft halo and the count, as standalone SVG. */
export function clusterBubbleSvg(count: number, pixelRatio = ASSET_MARKER_PIXEL_RATIO): string {
  const box = clusterBox(count);
  const c = box / 2;
  const r = clusterDiameter(count) / 2;
  const label = count > 999 ? `${Math.floor(count / 1000)}k` : String(count);
  const font = count < 100 ? 13 : 12;
  return (
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${box} ${box}" width="${box * pixelRatio}" height="${box * pixelRatio}">` +
    `<defs><filter id="s" x="-30%" y="-30%" width="160%" height="160%" color-interpolation-filters="sRGB">` +
    `<feDropShadow dx="0" dy="1" stdDeviation="1.2" flood-color="${ASSET_MARKER_OUTLINE}" flood-opacity="0.4"/></filter></defs>` +
    `<circle cx="${c}" cy="${c}" r="${r + 3.5}" fill="${ASSET_MARKER_HALO}" fill-opacity="0.42"/>` +
    `<g filter="url(#s)"><circle cx="${c}" cy="${c}" r="${r}" fill="${ASSET_MARKER_HALO}"/>` +
    `<circle cx="${c}" cy="${c}" r="${r - 2.2}" fill="#1e293b"/></g>` +
    `<text x="${c}" y="${c}" dy="0.35em" text-anchor="middle" font-family="Inter, system-ui, -apple-system, 'Segoe UI', sans-serif" font-size="${font}" font-weight="700" fill="${ASSET_MARKER_HALO}">${label}</text>` +
    `</svg>`
  );
}

/** The image's width / height in device pixels. */
export function assetMarkerImagePixels(spec: AssetMarkerImageSpec): number {
  const css = spec.part === 'cluster' ? clusterBox(spec.count) : spec.size;
  return css * ASSET_MARKER_PIXEL_RATIO;
}

/** The SVG markup for an image spec, drawn at the map pixel ratio. */
export function assetMarkerImageSvg(spec: AssetMarkerImageSpec): string {
  const px = assetMarkerImagePixels(spec);
  switch (spec.part) {
    case 'face':
      return assetMarkerSvg({
        part: 'face',
        kind: spec.kind,
        color: `#${spec.color}`,
        hollow: spec.hollow,
        // The face differs with a nose (no keyline of its own); any heading stands for "has one".
        heading: spec.nose ? 0 : null,
        moving: spec.nose,
        facing: spec.facing,
        size: px,
      });
    case 'nose':
      return assetMarkerSvg({ part: 'nose', color: `#${spec.color}`, heading: 0, size: px });
    case 'shadow':
      return assetMarkerSvg({
        part: 'shadow',
        heading: spec.shape === 'puck' ? 0 : null,
        size: px,
      });
    default:
      return clusterBubbleSvg(spec.count);
  }
}

// ── Feature properties ───────────────────────────────────────────────────────────────────────
/** What a vehicle feature carries for the marker layers. */
export interface AssetMarkerFeatureProps {
  amFace: string;
  /** '' when there is no nose (the nose layer filters on amNoseOn). */
  amNose: string;
  amShadow: string;
  amNoseOn: 0 | 1;
  /** Degrees clockwise from north (0 when unknown). */
  heading: number;
}

export interface AssetMarkerFeatureInput {
  kind?: AssetMarkerKind;
  /** The asset's status key (map/assetStatus): its colour, and hollow for "offline". */
  status?: AssetStatusKey;
  /** State colour (#rgb / #rrggbb); `status` wins when both are given. */
  color?: string | null;
  heading?: number | null;
  /** Draw the nose; defaults to "has a heading". */
  moving?: boolean;
  hollow?: boolean;
  /** Marker box in CSS px. */
  size: number;
}

export function assetMarkerFeatureProps({
  kind = 'truck',
  status,
  color: colorIn,
  heading = null,
  moving,
  hollow: hollowIn = false,
  size,
}: AssetMarkerFeatureInput): AssetMarkerFeatureProps {
  const color = status ? ASSET_STATUS[status].marker : colorIn;
  const hollow = hollowIn || (status ? !!ASSET_STATUS[status].hollow : false);
  const hex = (normaliseHex(color) ?? ASSET_MARKER_DEFAULT_COLOR).slice(1);
  const nose = assetMarkerHasNose({ heading, moving, hollow });
  const facing = assetMarkerFacing(heading, nose);
  return {
    amFace: assetMarkerImageId({ part: 'face', kind, color: hex, hollow, nose, facing, size }),
    amNose: nose ? assetMarkerImageId({ part: 'nose', color: hex, size }) : '',
    amShadow: assetMarkerImageId({ part: 'shadow', shape: nose ? 'puck' : 'disc', size }),
    amNoseOn: nose ? 1 : 0,
    heading: normaliseHeading(heading) ?? 0,
  };
}

// ── Images on demand ─────────────────────────────────────────────────────────────────────────
const decoded = new Map<string, HTMLImageElement>();
const decoding = new Map<string, Promise<HTMLImageElement>>();

function decode(id: string, spec: AssetMarkerImageSpec): Promise<HTMLImageElement> {
  const ready = decoded.get(id);
  if (ready) return Promise.resolve(ready);
  const pending = decoding.get(id);
  if (pending) return pending;
  const px = assetMarkerImagePixels(spec);
  const promise = new Promise<HTMLImageElement>((resolve, reject) => {
    const img = new Image(px, px);
    img.onload = () => {
      decoded.set(id, img);
      decoding.delete(id);
      resolve(img);
    };
    img.onerror = () => {
      decoding.delete(id);
      reject(new Error(`asset marker image ${id} did not load`));
    };
    img.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(assetMarkerImageSvg(spec))}`;
  });
  decoding.set(id, promise);
  return promise;
}

/**
 * `styleimagemissing` handler: adds an asset-marker image by name (a transparent placeholder
 * first when it is not decoded yet). Returns false for any other image, so callers can chain
 * their own handlers.
 */
export function addMissingAssetMarkerImage(map: MaplibreMap, id: string): boolean {
  const spec = parseAssetMarkerImageId(id);
  if (!spec) return false;
  if (typeof document === 'undefined') return true;
  const opts = { pixelRatio: ASSET_MARKER_PIXEL_RATIO };
  try {
    const ready = decoded.get(id);
    if (ready) {
      if (!map.hasImage(id)) map.addImage(id, ready, opts);
      return true;
    }
    if (!map.hasImage(id)) {
      const px = assetMarkerImagePixels(spec);
      map.addImage(id, { width: px, height: px, data: new Uint8Array(px * px * 4) }, opts);
    }
  } catch {
    return true; // map disposed mid-load
  }
  decode(id, spec).then(
    (img) => {
      try {
        if (map.hasImage(id)) map.updateImage(id, img);
        else map.addImage(id, img, opts);
      } catch {
        /* map disposed, or the style swapped meanwhile (the next miss re-adds it) */
      }
    },
    () => {}
  );
  return true;
}

const installed = new WeakSet<MaplibreMap>();
/** Draw asset-marker images on demand on this map (idempotent per map instance). */
export function installAssetMarkerImages(map: MaplibreMap): void {
  if (installed.has(map)) return;
  installed.add(map);
  map.on('styleimagemissing', (e: { id: string }) => {
    addMissingAssetMarkerImage(map, e.id);
  });
}

// ── Layers ───────────────────────────────────────────────────────────────────────────────────
export type AssetSymbolLayer = Omit<SymbolLayerSpecification, 'source'>;
export type AssetCircleLayer = Omit<CircleLayerSpecification, 'source'>;

/** Points that are not cluster bubbles. */
export const NOT_CLUSTER: FilterSpecification = ['!', ['has', 'point_count']];

const both = (
  a: FilterSpecification | undefined,
  b: ExpressionSpecification
): FilterSpecification => (a ? (['all', a, b] as FilterSpecification) : b);

/**
 * The three marker layers (shadow, nose, face) for a source whose features carry
 * assetMarkerFeatureProps. `prefix` names them `<prefix>-shadow` / `-nose` / `-face`; the face
 * is the one to make interactive.
 */
export function assetMarkerLayers(
  prefix: string,
  { filter, visible = true }: { filter?: FilterSpecification; visible?: boolean } = {}
): AssetSymbolLayer[] {
  const visibility = visible ? 'visible' : 'none';
  const common = {
    'icon-allow-overlap': true,
    'icon-ignore-placement': true,
    visibility,
  } as const;
  const turned = {
    'icon-rotate': ['get', 'heading'] as ExpressionSpecification,
    'icon-rotation-alignment': 'map',
  } as const;
  return [
    {
      id: `${prefix}-shadow`,
      type: 'symbol',
      ...(filter ? { filter } : {}),
      layout: { ...common, ...turned, 'icon-image': ['get', 'amShadow'] },
      paint: { 'icon-translate': [0, 1.4], 'icon-translate-anchor': 'viewport' },
    },
    {
      id: `${prefix}-nose`,
      type: 'symbol',
      filter: both(filter, ['==', ['get', 'amNoseOn'], 1]),
      layout: { ...common, ...turned, 'icon-image': ['get', 'amNose'] },
    },
    {
      id: `${prefix}-face`,
      type: 'symbol',
      ...(filter ? { filter } : {}),
      layout: { ...common, 'icon-image': ['get', 'amFace'], 'icon-rotation-alignment': 'viewport' },
    },
  ];
}

/** A teal ring under a selected marker (`radius` in CSS px). */
export function assetMarkerSelectionLayer(
  id: string,
  { radius, filter }: { radius: number; filter?: FilterSpecification }
): AssetCircleLayer {
  return {
    id,
    type: 'circle',
    ...(filter ? { filter } : {}),
    paint: {
      'circle-radius': radius,
      'circle-color': ASSET_MARKER_SELECTED,
      'circle-opacity': 0.2,
      'circle-stroke-color': ASSET_MARKER_SELECTED,
      'circle-stroke-width': 2.5,
      'circle-stroke-opacity': 0.95,
    },
  };
}

/** Cluster bubbles for a clustered GeoJSON source (count drawn into the image). */
export function assetClusterLayer(id: string): AssetSymbolLayer {
  return {
    id,
    type: 'symbol',
    filter: ['has', 'point_count'],
    layout: {
      'icon-image': [
        'concat',
        `${ASSET_MARKER_IMAGE_PREFIX}cluster/`,
        ['to-string', ['get', 'point_count']],
      ],
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
  };
}
